import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import './globalSearch.css';

/**
 * Global search (VERIFICATION_PLAN P3) — one «جستجو» in the app bar over the indexed ledger of every
 * entity. Debounced type-ahead, results grouped by type, click jumps to the entity's screen. Ctrl/Cmd-K
 * focuses it. The server does the Persian normalization + ranking; this stays a thin, fast client.
 */

export type SearchType =
  'deal' | 'customer' | 'cheque' | 'po' | 'supplier' | 'product' | 'release' | 'help';
interface Hit {
  type: SearchType;
  id: number;
  title: string;
  subtitle: string;
}
interface Group {
  type: SearchType;
  label: string;
  items: Hit[];
}

const ICON: Record<SearchType, string> = {
  deal: '📄',
  customer: '🏢',
  cheque: '🧾',
  po: '🛒',
  supplier: '🏭',
  product: '⚙️',
  release: '🚚',
  help: '❓',
};

export function GlobalSearch({ onJump }: { onJump: (type: SearchType, id: number) => void }) {
  const [q, setQ] = useState('');
  const [groups, setGroups] = useState<Group[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounced fetch.
  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) {
      setGroups([]);
      return;
    }
    let alive = true;
    const t = setTimeout(async () => {
      const r = await api<{ groups: Group[] }>(`/api/search?q=${encodeURIComponent(query)}`);
      if (alive && r.ok) {
        setGroups((r.body as { groups: Group[] }).groups ?? []);
        setActive(0);
        setOpen(true);
      }
    }, 180);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q]);

  // Ctrl/Cmd-K focuses the box from anywhere; click-outside + Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, []);

  const flat = groups.flatMap((g) => g.items.map((it) => ({ ...it })));
  const pick = (h: Hit) => {
    onJump(h.type, h.id);
    setOpen(false);
    setQ('');
    setGroups([]);
    inputRef.current?.blur();
  };

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') return setOpen(false);
    if (!open || flat.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const h = flat[active];
      if (h) pick(h);
    }
  };

  let idx = -1;
  return (
    <div className="gsearch" ref={boxRef}>
      <input
        ref={inputRef}
        className="gsearch-input"
        value={q}
        placeholder="جستجو… (Ctrl+K)"
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => groups.length && setOpen(true)}
        onKeyDown={onInputKey}
      />
      {open && q.trim().length >= 2 ? (
        <div className="gsearch-pop">
          {flat.length === 0 ? (
            <div className="gsearch-empty">چیزی یافت نشد</div>
          ) : (
            groups.map((g) => (
              <div key={g.type} className="gsearch-group">
                <div className="gsearch-group-label">
                  {ICON[g.type]} {g.label}
                </div>
                {g.items.map((h) => {
                  idx++;
                  const i = idx;
                  return (
                    <button
                      key={`${h.type}-${h.id}`}
                      type="button"
                      className={`gsearch-item${i === active ? ' active' : ''}`}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => pick(h)}
                    >
                      <span className="gsearch-title">{h.title}</span>
                      {h.subtitle ? <span className="gsearch-sub">{h.subtitle}</span> : null}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
